import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import logger from "@insite/client-framework/Logger";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import clearProduct from "@insite/client-framework/Store/Components/ProductSelector/Handlers/ClearProduct";
import reset from "@insite/client-framework/Store/Components/ProductSelector/Handlers/Reset";
import searchProducts from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SearchProducts";
import setProduct from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SetProduct";
import setUnitOfMeasure from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SetUnitOfMeasure";
import { getProductSelector } from "@insite/client-framework/Store/Components/ProductSelector/ProductSelectorSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import Search from "@insite/mobius/Icons/Search";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import TextField, { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import ToasterContext, { HasToasterContext } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import addToCartTracking from "@MasterBluePrint/Tracking/AddToCart/addToCartTracking";
import debounce from "lodash/debounce";
import React, { ChangeEvent, ReactNode, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    skipInventoryValidation?: boolean;
    selectButtonTitle?: string;
    productIsConfigurableMessage?: ReactNode;
    productIsUnavailableMessage?: ReactNode;
    customErrorMessage?: ReactNode;
    extendedStyles?: ProductSelectorStyles;
}

const mapStateToProps = (state: ApplicationState) => {
    const { isSearching, searchResults, selectedProductInfo, errorType } = getProductSelector(state);
    const settingsCollection = getSettingsCollection(state);
    const product = getProductState(state, selectedProductInfo?.productId).value;
    return {
        isSearching,
        searchResults,
        selectedProductInfo,
        product,
        errorType,
        location: getLocation(state),
        canOrderUpload: settingsCollection.orderSettings.canOrderUpload,
        showAddToCartConfirmationDialog: settingsCollection.productSettings.showAddToCartConfirmationDialog,
        orderUploadPageNavLink: getPageLinkByPageType(state, "OrderUploadPage"),
        addingProductToCart: state.context.addingProductToCart,
        state,
    };
};

const mapDispatchToProps = {
    searchProducts,
    setProduct,
    clearProduct,
    setUnitOfMeasure,
    reset,
    addToCart: makeHandlerChainAwaitable(addToCart),
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductSelectorStyles {
    container?: GridContainerProps;
    searchGridItem?: GridItemProps;
    qtyGridItem?: GridItemProps;
    optionWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    productImage?: LazyImageProps;
    infoWrapper?: InjectableCss;
    autocompleteTitleText?: TypographyPresentationProps;
    autocompleteErpText?: TypographyPresentationProps;
    selectButton?: ButtonPresentationProps;
    searchDynamicDropdown?: DynamicDropdownPresentationProps;
    buttonsWrapper?: InjectableCss;
    qtyTextField?: TextFieldProps;
    bulkOrderButton?: ButtonPresentationProps;
    addToCartButton?: ButtonPresentationProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
}

export const productSelectorStyles: ProductSelectorStyles = {


    container: {
        gap: 10,
        css: css`
            > div {
                width: 100% !important;
            }
        `,
    },
    titleGridItem: {
        width: 8,
        css: css`
            padding-right: .625rem;
            text-align: left;
        `,
    },
    titleText: {
        variant: "h3",
        css: css`
            padding-right: .625rem;
            padding-top: .625rem;
            text-align: left;
            color: #002554;
            font-family: 'Roboto', sans-serif;
            font-size: 1.125rem;
            line-height: 1.9375rem;
            > span {
                bottom: 1rem;
                position: relative;
            }
        `,
    },
    searchGridItem: {
        width: 8,
        css: css`
            padding-right: .625rem;
            text-align: left;
            bottom: 1.75rem;
            position: relative;
            > div > div > div > span {
                right: 1.75rem;
                position: relative;
                top: 2.813rem;
            }
        `,
    },
    qtyGridItem: {
        width: 3,
        css: css`
            padding-right: .625rem;
            text-align: left;
            bottom: 1.75rem;
            padding: relative;
        `,
    },
    buttonsWrapper: {
        css: css`
            text-align: center;
            width: 8.75rem;
            position: relative;
        `,
    },
    bulkOrderButton: {
        css: css`
            box-sizing: border-box;
            margin-left: .5rem;
            padding: 0;
            height: 2.5rem;
            width: 9.75rem !important;
            font-family: 'Roboto', sans-serif;
            font-size: .875rem;
            line-height: 1.3125rem;
            text-align: center;
            border: 1px solid #002554;
            border-radius: .125rem;
            background-color: #ffffff;
            color: #002554;
            &:hover {
                background-color: #2060ad;
                color: #ddecfe;
                border: 1px solid #2060ad;
            }
            > span {
                font-weight: inherit !important;
            }
        `,
    },
    addToCartButton: {
        css: css`
            box-sizing: border-box;
            height: 2.5rem;
            width: 9.75rem !important;
            border-radius: .125rem;
            font-family: 'Roboto', sans-serif;
            font-size: .875rem;
            font-weight: bold;
            margin-left: .31250625rem;
            line-height: 1.25rem;
            text-align: center;
            background-color: #ffb600;
            border-style: none;
            color: #002554;

            &:hover {
                background-color: #ff9400;
                color: #002554;
            }
        `,
    },
    searchDynamicDropdown: {
        cssOverrides: {
            formField: css`
                color: #424b5a;
                font-family: Lato;
                font-size: .9375rem;
                letter-spacing: .011rem;
                line-height: 1.5rem;
                > div > input {
                    letter-spacing: .01rem;
                    line-height: 1.125rem;
                    color: #424b5a !important;
                    font-family: Lato !important;
                    font-size: .875rem !important;
                    border: 1px solid rgba(32, 96, 173, .5) !important;
                    border-radius: 2px !important;
                    background-color: rgba(224, 231, 255, .5) !important;
                    height: 2.5rem;
                }
                > div > div > span {
                    margin-bottom: -1.563rem;
                }
                > label {
                    font-weight: 500;
                    color: #424b5a;
                }
            `,
        },
    },
    qtyTextField: {
        cssOverrides: {
            formField: css`
                color: #424b5a;
                font-family: Lato;
                font-size: .9375rem;
                letter-spacing: .011rem;
                line-height: 1.5rem;
                > div > input {
                    border: 1px solid rgba(32, 96, 173, .5) !important;
                    border-radius: 2px !important;
                    background-color: rgba(224, 231, 255, .5) !important;
                    height: 2.5rem;
                    color: #424b5a !important;
                }
                > label {
                    font-weight: 500;
                    color: #424b5a;
                }
            `,
        },
    },
    optionWrapper: {
        css: css`
            display: flex;
            cursor: pointer;
        `,
    },
    imageWrapper: {
        css: css`
            display: flex;
            width: 3.125rem;
            height: 3.125rem;
            margin-left: .625rem;
        `,
    },
    productImage: {
        width: "3.125rem",
        css: css`
            flex-shrink: 0;
            img {
                height: 100%;
            }
        `,
    },
    infoWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    autocompleteTitleText: {
        size: 14,
    },
    autocompleteErpText: {
        size: 14,
        css: css`
            margin-top: .31250625rem;
        `,
    },
    selectButton: {
        css: css`
            margin-left: .625rem;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            width: 100%;
                        `,
                    ],
                    "max",
                )}
        `,
    },
};

const ENTER_KEY = 13;

const CustomProductSelector = ({
    skipInventoryValidation,
    selectButtonTitle,
    searchProducts,
    searchResults,
    setProduct,
    clearProduct,
    selectedProductInfo,
    product,
    errorType,
    extendedStyles,
    customErrorMessage,
    productIsConfigurableMessage,
    productIsUnavailableMessage,
    location,
    reset,
    canOrderUpload,
    orderUploadPageNavLink,
    addToCart,
    showAddToCartConfirmationDialog,
    addingProductToCart,
    state,
}: Props) => {
    const [qty, setQty] = useState("1");
    const [errorMessage, setErrorMessage] = useState<ReactNode>("");
    const [qtyErrorMessage, setQtyErrorMessage] = useState<ReactNode>("");
    const [selectedProductId, setSelectedProductId] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [styles] = useState(() => mergeToNew(productSelectorStyles, extendedStyles));
    const toasterContext = React.useContext(ToasterContext);
    const [productQty, setProductQty] = useState("1");

    useEffect(() => {
        setSearchTerm(selectedProductId);
        reset();
    }, [location.pathname]);

    useEffect(() => {
        setErrorMessage(customErrorMessage);
    }, [customErrorMessage]);

    useEffect(() => {
        setSelectedProductId(selectedProductInfo ? selectedProductInfo.productId : "");
        if (selectedProductInfo) {
            const newProductInfo = { ...selectedProductInfo };
            newProductInfo.qtyOrdered = Math.max(Number(qty) || 1, product?.minimumOrderQty || 1);
            addProductToCart(newProductInfo);
            clearProduct();
            setQty("");
        }
    }, [selectedProductInfo]);

    useEffect(() => {
        switch (errorType) {
            case "productIsConfigurable":
                setErrorMessage(productIsConfigurableMessage || translate("Cannot select configurable products"));
                break;
            case "productIsUnavailable":
                setErrorMessage(productIsUnavailableMessage || translate("Product is unavailable"));
                break;
            default:
                setErrorMessage("");
                break;
        }
    }, [errorType]);

    const filterResult = () => {
        if (searchResults) {
            const product = searchResults.find(o => o.erpNumber?.toLowerCase() === searchTerm?.toLowerCase());
            if (product) {
                const variantParentId = product?.styleParentId;
                if (variantParentId) {
                    setProduct({
                        productId: product?.id?.toString(),
                        variantId: variantParentId,
                        validateProduct: true,
                        skipInventoryValidation,
                    });
                } else {
                    setProduct({
                        productId: product?.id?.toString(),
                        validateProduct: true,
                        skipInventoryValidation,
                    });
                }
            } else {
                setErrorMessage(siteMessage("Product_NotFound"));
            }
        }
    };

    const debouncedSearchProducts = debounce((query: string) => {
        searchProducts({ query });
    }, 300);

    const onInputChanged = (event: ChangeEvent<HTMLInputElement>) => {
        setErrorMessage("");
        setSearchTerm(event.target.value);

        if (selectedProductInfo) {
            clearProduct();
        }
        debouncedSearchProducts(event.target.value);
    };
    const validateQty = () => {
        if (!qty || qty.match(/^[0-9]+$/) == null) {
            setQtyErrorMessage(siteMessage("Product_QtyInvalidMessage") || translate("Quantity is invalid."));
            return false;
        }
        setQtyErrorMessage("");
        return true;
    };

    const selectProduct = () => {
        if (!validateQty()) {
            return;
        }
        filterResult();

        if (!selectedProductInfo || errorMessage || !product) {
            return;
        }
        clearProduct();

        searchProducts({ query: "" });
    };

    const onKeyPress = (event: React.KeyboardEvent) => {
        if (event.charCode === ENTER_KEY) {
            selectProduct();
        }
    };
    const addProductToCart = async (productInfo: ProductInfo) => {
        const cartline = (await addToCart({
            productId: productInfo.productId,
            qtyOrdered: productInfo.qtyOrdered,
            unitOfMeasure: productInfo.unitOfMeasure,
            onError: (error: any) => {

                toasterContext.addToast({
                    body: error,
                    messageType: "danger",

                });

            },
        })) as CartLineModel;
        if (cartline && cartline.id) {
            addToCartTracking(cartline, productInfo, state);
        }
        if (showAddToCartConfirmationDialog) {
            toasterContext.addToast({
                body: <ProductAddedToCartMessage isQtyAdjusted={cartline.isQtyAdjusted} multipleProducts={false} />,
                messageType: "success",
            });
        }
    };

    const qtyChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value.replace(/^0+/, '');
        setProductQty(value);
        setQty(value);
    };
    const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === ".") {
            event.preventDefault();
        }
    };

    return (
        <GridContainer gap={12} data-test-selector="productSelector">
            <GridItem width={12}>
                <Typography {...styles.titleText}>
                    {translate("Quick Buy")}
                    <span>
                        <svg className="mb-3" width="20" height="3" xmlns="http://www.w3.org/2000/svg">
                            <path d="M0 3V0h20v3z" fill="#C63527" fillRule="nonzero"></path>
                        </svg>
                    </span>
                </Typography>
            </GridItem>

            <GridItem {...styles.searchGridItem} width={12}>
                <GridContainer gap={8}>
                    <GridItem width={8}>
                        <TextField
                            {...styles.searchDynamicDropdown}
                            label={siteMessage("QuickBuy_SearchLabel")}
                            onChange={onInputChanged}
                            onKeyPress={onKeyPress}
                            error={errorMessage}
                            data-test-selector="productSelector_search"
                        />
                        <Icon onClick={() => selectProduct()} src="Search" size={18} color="#002554" />
                    </GridItem>
                    <GridItem width={4}>
                        <TextField
                            {...styles.qtyTextField}
                            label={translate("Qty:")}
                            onChange={qtyChangeHandler}
                            onKeyPress={keyPressHandler}
                            onBlur={validateQty}
                            error={qtyErrorMessage}
                            data-test-selector="productSelector_qty"
                            value={productQty}
                        />
                    </GridItem>
                </GridContainer>
            </GridItem>
            <GridItem width={12}>
                <GridContainer gap={12}>
                    <GridItem width={6} {...styles.buttonsWrapper}>
                        <Button
                            {...styles.addToCartButton}
                            onClick={() => selectProduct()}
                            data-test-selector="productSelector_selectProduct"
                        >
                            {selectButtonTitle || translate("Select Product")} +
                        </Button>
                    </GridItem>
                    <GridItem width={6} {...styles.buttonsWrapper}>
                        {canOrderUpload && (
                            <Link href={orderUploadPageNavLink ? orderUploadPageNavLink.url : undefined}>
                                <Button {...styles.bulkOrderButton}>{translate("Bulk Order")}</Button>
                            </Link>
                        )}
                    </GridItem>
                </GridContainer>
            </GridItem>
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(CustomProductSelector);
